import {SectionItem} from 'common/components/items-menu/types';
import {ToastType} from 'common/components/toast/types';

export enum ResourceType {
  Event = `event`,
  User = `user`,
  Cohort = `cohort`,
  Other = `other`,
}

/** List of project ids for internal testing(e.g., api migrations, enabling features) */
export const INTERNAL_PROJECTS = new Set([3, 4, 132990, 1297132]);

export const DEFAULT_CHART_HEIGHT = 600;

// amount to time to show the completed, or failed toast before closing the original downloading toast
export const DOWNLOAD_TOAST_OVERLAP_MS = 1500;

export const DOWNLOAD_SUCCESS_TOAST = {
  type: ToastType.SUCCESS,
  message: `Download Complete`,
};

export const DOWNLOAD_FAIL_TOAST = {
  type: ToastType.ERROR,
  message: `Download Failed`,
};

export enum Loading {
  Bookmarks = `bookmarks`,
  BookmarkedReport = `bookmarkedReport`,
  Download = `download`,
  Result = `result`,
}

export enum ChartType {
  Bar = `bar`,
  Line = `line`,
  Metric = `metric`,
  Pie = `pie`,
  StackedBar = `bar-stacked`,
  StackedLine = `stacked-line`,
  Sankey = `sankey`,
  Table = `table`,
  FunnelSteps = `funnel-steps`,
  FunnelTrend = `funnel-trend`,
  FunnelFrequencyLine = `funnel-frequency-line`,
  FunnelTTCLine = `funnel-ttc-line`,
  FunnelMedianTTC = `funnel-median-ttc`,
  FunnelDropOff = `funnel-drop-off`,
  RetentionBar = `bar-retention`,
  RetentionLine = `line-retention`,
  Frequency = `frequency`,
  TopPaths = `top-paths`, // alias for Sankey
  Flows = `flows`, // alias for Sankey
  FlowsTopPaths = `paths`,
  ImpactAdoption = `impact-adoption`,
  ImpactTrends = `impact-trends`,
  ImpactPropensity = `impact-propensity`,
}

export const ChartTypeSelection: {[key in ChartType]: SectionItem} = {
  [ChartType.Bar]: {
    icon: `bar`,
    label: `Bar`,
    value: ChartType.Bar,
  },
  [ChartType.Line]: {
    icon: `line`,
    label: `Line`,
    value: ChartType.Line,
  },
  [ChartType.Metric]: {
    icon: `single-metric`,
    label: `Metric`,
    value: ChartType.Metric,
  },
  [ChartType.Pie]: {
    icon: `pie`,
    label: `Pie`,
    value: ChartType.Pie,
  },
  [ChartType.StackedBar]: {
    icon: `bar-stacked`,
    label: `Stacked Bar`,
    value: ChartType.StackedBar,
  },
  [ChartType.StackedLine]: {
    icon: `stacked-line`,
    label: `Stacked Line`,
    value: ChartType.StackedLine,
  },
  [ChartType.Sankey]: {
    icon: `top-paths`,
    label: `Top Paths`,
    value: ChartType.Sankey,
  },
  [ChartType.Table]: {
    icon: `table`,
    label: `Table`,
    value: ChartType.Table,
  },
  [ChartType.FunnelSteps]: {
    icon: `funnel-steps`,
    label: `Funnel Steps`,
    value: ChartType.FunnelSteps,
  },
  [ChartType.FunnelTrend]: {
    icon: `line`,
    label: `Funnel Trends`,
    value: ChartType.FunnelTrend,
  },
  [ChartType.FunnelFrequencyLine]: {
    icon: `line`,
    label: `Funnel Frequency Line`,
    value: ChartType.FunnelFrequencyLine,
  },
  [ChartType.FunnelTTCLine]: {
    icon: `line`,
    label: `Funnel Time To Convert Line`,
    value: ChartType.FunnelTTCLine,
  },
  [ChartType.FunnelMedianTTC]: {
    icon: `bar-stacked`,
    label: `Time to Convert`,
    value: ChartType.FunnelMedianTTC,
  },
  [ChartType.FunnelDropOff]: {
    icon: `top-paths`,
    label: `Conversion Flows`,
    value: ChartType.FunnelDropOff,
  },
  [ChartType.RetentionBar]: {
    icon: `bar`,
    label: `Bar`,
    value: ChartType.RetentionBar,
  },
  [ChartType.RetentionLine]: {
    icon: `line`,
    label: `Line`,
    value: ChartType.RetentionLine,
  },
  [ChartType.Frequency]: {
    icon: `bar`,
    label: `Frequency`,
    value: ChartType.Frequency,
  },
  [ChartType.TopPaths]: {
    icon: `top-paths`,
    label: `Top Paths`,
    value: ChartType.Sankey, // Alias
  },
  [ChartType.FlowsTopPaths]: {
    icon: `funnel-steps`,
    label: `Top Paths`,
    value: ChartType.FlowsTopPaths,
  },
  [ChartType.Flows]: {
    icon: `top-paths`,
    label: `User Flows`,
    value: ChartType.Sankey, // Alias
  },
  [ChartType.ImpactAdoption]: {
    icon: `line`,
    label: `Adoption Over Time`,
    value: ChartType.ImpactAdoption,
  },
  [ChartType.ImpactTrends]: {
    icon: `stacked-line`,
    label: `Impact Trends`,
    value: ChartType.ImpactTrends,
  },
  [ChartType.ImpactPropensity]: {
    icon: `bar-stacked`,
    label: `Causal Impact`,
    value: ChartType.ImpactPropensity,
  },
};

/**
 * Types of values available on line charts.
 * The type indicates what the value represents and helps determine
 * how the value should be formatted when displayed.
 */
export enum LineChartValueType {
  /** a percentage value */
  Percent = `percent`,
  /** the length of time elapsed */
  Duration = `duration`,
  /** a simple number value (usually to be formatted with commas) */
  Simple = `simple`,
}

/** Relative or absolute */
export enum ValueRepresentationType {
  Absolute = `absolute`,
  Relative = `relative`,
}

export const ValueRepresentationTypeSelection: {[key in ValueRepresentationType]: SectionItem} = {
  [ValueRepresentationType.Absolute]: {
    icon: `value-absolute`,
    label: `Absolute`,
    value: ValueRepresentationType.Absolute,
  },
  [ValueRepresentationType.Relative]: {
    icon: `value-relative`,
    label: `Relative`,
    value: ValueRepresentationType.Relative,
  },
};

export const SESSION_START_EVENT_NAME = `$session_start`;
export const SESSION_END_EVENT_NAME = `$session_end`;
export const SESSION_EVENT_NAMES = Object.freeze({
  start: SESSION_START_EVENT_NAME,
  end: SESSION_END_EVENT_NAME,
});
export const SESSION_EVENTS = Object.freeze({
  [SESSION_START_EVENT_NAME]: {session_event: `start`},
  [SESSION_END_EVENT_NAME]: {session_event: `end`},
});

export const SPECIAL_EVENTS = {
  ALL_EVENTS: `$all_events`,
  TOP_EVENTS: `$top_events`,
  ANY_EVENT: `$mp_anything_event`,
  SESSION_START: SESSION_START_EVENT_NAME,
  SESSION_END: SESSION_END_EVENT_NAME,
};

/* eslint-disable camelcase */
export const EVENTS = {
  [SPECIAL_EVENTS.ALL_EVENTS]: `All Events`,
  [SPECIAL_EVENTS.TOP_EVENTS]: `Your Top Events`,
  $ae_crashed: `App Crashed`,
  $ae_first_open: `First App Open`,
  $ae_iap: `In-App Purchase`,
  $ae_notif_opened: `Push Notification Opened`,
  $ae_session: `App Session`,
  $ae_updated: `App Updated`,
  $campaign_bounced: `Message Bounced`,
  $campaign_delivery: `Message Sent`,
  $campaign_marked_spam: `Message Marked Spam`,
  $campaign_link_click: `Message Link Clicked`,
  $campaign_open: `Message Opened`,
  $campaign_received: `Message Received`,
  $create_alias: `Create Alias`,
  $experiment_started: `Experiment Started`,
  $identify: `Identify`,
  $journey_entered: `Campaign Entered`,
  $message_suppressed: `Message Suppressed`,
  $session_end: `Session End`,
  $session_start: `Session Start`,
  $show_survey: `Show Survey`,
  $signup: `Signup`,
};
/* eslint-enable camelcase */

/* eslint-disable camelcase */
export const PROPERTIES = {
  $ae_crashed_reason: `Crash Reason`,
  $ae_first_app_open_date: `First App Open Date`,
  $ae_iap_name: `Product Name`,
  $ae_iap_price: `Product Price`,
  $ae_iap_quantity: `Product Quantity`,
  $ae_notif_message: `Message`,
  $ae_session_length: `Session Length`,
  $ae_total_app_session_length: `Total App Session Length`,
  $ae_total_app_sessions: `Total App Sessions`,
  $ae_updated_version: `Version Updated`,
  $android_app_version: `Android App Version`,
  $android_app_version_code: `Android App Version Code`,
  $android_brand: `Android Brand`,
  $android_devices: `Android Devices`,
  $android_lib_version: `Android Lib Version`,
  $android_manufacturer: `$android_manufacturer`,
  $android_model: `Android Model`,
  $android_notification_id: `Android Notification Id`,
  $android_os: `Android OS`,
  $android_os_version: `Android OS Version`,
  $android_push_error: `$android_push_error`,
  $anon_id: `Anonymous ID`,
  $answer_count: `Answer Count`,
  $answers: `$answers`,
  $app_build_number: `App Build Number`,
  $app_release: `App Release`,
  $app_version: `App Version`,
  $app_version_string: `App Version`,
  $bluetooth_enabled: `Bluetooth Enabled`,
  $bluetooth_version: `Bluetooth Version`,
  $bounce_category: `Bounce Category`,
  $bounce_notification: `$bounce_notification`,
  $bounced: `$bounced`,
  $bounced_reason: `$bounced_reason`,
  $brand: `Brand`,
  $browser: `Browser`,
  $browser_version: `Browser Version`,
  $bucket: `Bucket`,
  $button_id: `Button Id`,
  $button_label: `Button Label`,
  $campaigns: `Messages Sent`,
  $cancelled: `$cancelled`,
  $carrier: `Carrier`,
  $city: `City`,
  $country_code: `Country Code`,
  $created: `Created`,
  $current_url: `Current URL`,
  $dataset: `Project Name`,
  $deliveries: `Deliveries`,
  $device: `Device`,
  $device_id: `Device ID`,
  $distinct_id: `Distinct ID`,
  $distinct_id_before_identity: `Distinct ID Before Identity`,
  $duration: `Duration`,
  $duration_s: `Session Duration (Seconds)`,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  $el_attr__href: `$el_attr__href`,
  $el_text: `$el_text`,
  $email: `Email`,
  $event_count: `Session Event Count`,
  $event_name: `Event Name`,
  $events_distinct_id: `$events_distinct_id`,
  $experiment_id: `Experiment ID`,
  $experiments: `Experiments`,
  $failure_description: `Failure Description`,
  $failure_reason: `Failure Reason`,
  $first_name: `First name`,
  $from_binding: `From Binding`,
  $from_preview: `Sent as preview`,
  $google_play_services: `Google Play Services`,
  $had_persisted_distinct_id: `Had Persisted Distinct Id`,
  $has_nfc: `Has NFC`,
  $has_telephone: `Has Telephone`,
  $histogram: `Distribution`,
  $host: `Host`,
  $identified_id: `Identified ID`,
  $ignore_time: `$ignore_time`,
  $import: `Import`,
  $initial_referrer: `Initial Referrer`,
  $initial_referring_domain: `Initial Referring Domain`,
  $insert_id: `Insert ID`,
  $ios_app_release: `iOS App Release`,
  $ios_app_version: `iOS App Version`,
  $ios_device_model: `iOS Device Model`,
  $ios_devices: `iOS Devices`,
  $ios_ifa: `iOS IFA`,
  $ios_lib_version: `iOS Lib Version`,
  $ios_notification_id: `iOS Notification Id`,
  $ios_version: `iOS Version`,
  $ip_hash: `$ip_hash`,
  $is_reshuffled: `$is_reshuffled`,
  $is_sticky: `Is Sticky Notification`,
  $journey_controls: `Campaign Controls`,
  $journey_id: `Campaign`,
  $journeys: `Journeys`,
  $last_login: `Last login`,
  $last_name: `Last Name`,
  $last_seen: `Updated at`,
  $last_successful_email: `$last_successful_email`,
  $lib_version: `Library Version`,
  $manufacturer: `Manufacturer`,
  $marked_spam: `$marked_spam`,
  $message_id: `Message ID`,
  $model: `Model`,
  $mp_first_event_time: `First Seen`,
  $mp_message_sends: `$mp_message_sends`,
  $name: `Name`,
  $neighborhood: `$neighborhood`,
  $notifications: `$notifications`,
  $origin_start: `Session Start Event Name`,
  $origin_end: `Session End Event Name`,
  $os: `Operating System`,
  $os_version: `OS Version`,
  $pathname: `Path Name`,
  $phone: `Phone`,
  $predict_event: `Predict event`,
  $predict_grade: `$predict_grade`,
  $predict_score: `$predict_score`,
  $radio: `Radio`,
  $referrer: `Referrer`,
  $referring_domain: `Referring Domain`,
  $region: `Region`,
  $responses: `$responses`,
  $screen_dpi: `Screen DPI`,
  $screen_height: `Screen Height`,
  $screen_width: `Screen Width`,
  $search_engine: `Search Engine`,
  $survey_collections: `$survey_collections`,
  $survey_responses: `$survey_responses`,
  $survey_shown: `Survey Shown`,
  $surveys: `Surveys Received`,
  $tap_action_type: `Tap Action Type`,
  $tap_action_uri: `Tap Action URI`,
  $tap_target: `Tap Target`,
  $time: `$time`,
  $timezone: `timezone`,
  $title: `title`,
  $transactions: `Transactions`,
  $unsubscribed: `Unsubscribed`,
  $user_id: `User ID`,
  $username: `Username`,
  $variant_id: `Variant`,
  $watch_model: `watch model`,
  $webdashv2: `$webdashv2`,
  $wifi: `Wifi`,
  alias: `alias`,
  bucket: `bucket`,
  button: `button`,
  campaign_id: `Message`,
  collection_id: `collection ID`,
  distinct_id: `Distinct ID`,
  has_nfc: `has nfc`,
  ip: `ip`,
  message_id: `variant`,
  message_subtype: `message subtype`,
  message_type: `message type`,
  mp_country_code: `Country`,
  mp_device_model: `Device Model`,
  mp_keyword: `search keyword`,
  mp_lib: `mixpanel library`,
  mp_processing_time: `time processed`,
  mp_processing_time_ms: `time processed`,
  sent_to: `sent to`,
  survey_id: `survey ID`,
  time: `Time`,
  token: `token`,
  utm_campaign: `utm campaign`,
  utm_content: `utm content`,
  utm_medium: `utm medium`,
  utm_source: `utm source`,
  utm_term: `utm term`,

  // the following are no longer used but
  // should be included for historical reasons
  mp_browser: `Browser`,
  mp_page: `Page View`,
  mp_platform: `Platform`,
  mp_referrer: `Referrer`,
};
/* eslint-enable camelcase */

export const NOT_APPLICABLE_PROPERTY_VALUE = `$not_applicable`;

export const DATASET_PROPERTY_NAME = `$dataset`;

export enum ProjectType {
  Normal = 1,
  Rollup = 3,
}

export const DATASET_MIXPANEL = `$mixpanel`;
export const DATASET_SALESFORCE = `salesforce`;
export const DATASET_ZENDESK = `zendesk`;

export const DATASET_SALESFORCE_CONTACTS = `contacts`;
export const DATASET_SALESFORCE_LEADS = `leads`;

export const PROPERTY_TYPES = {
  BOOLEAN: `boolean`,
  DATETIME: `datetime`,
  LIST: `list`,
  NUMBER: `number`,
  OBJECT: `object`,
  STRING: `string`,
  DIMENSION: `dimension`,
};

export enum PropertyType {
  Boolean = `boolean`,
  Datetime = `datetime`,
  List = `list`,
  Number = `number`,
  String = `string`,
  Object = `object`,
  Dimension = `dimension`,
  NonProperty = `other`,
}

export const TYPECAST_PROPERTY_TYPES = {
  STRING: `string`,
  NUMBER: `number`,
  BOOLEAN: `boolean`,
  DATETIME: `datetime`,
  LIST: `list`,
};

export const NUMERIC_PROPERTY_TYPES = [PROPERTY_TYPES.BOOLEAN, PROPERTY_TYPES.DATETIME, PROPERTY_TYPES.NUMBER];

export const STRING_TYPECAST_PROPERTIES = [`message_id`, `campaign_id`, `$experiment_id`, `$variant_id`];

export enum MappedPropertyValues {
  Campaigns = `$campaigns`,
  CampaignId = `campaign_id`,
  Dataset = `$dataset`,
  ExperimentId = `$experiment_id`,
  MessageId = `message_id`,
  VariantId = `$variant_id`,
}

export enum PRIMATIVE_MATH_TYPES {
  TOTAL = `total`,
  UNIQUE = `unique`,
  SESSIONS = `sessions`,
  AVERAGE = `average`,
  MEDIAN = `median`,
  P25 = `p25`,
  P75 = `p75`,
  P90 = `p90`,
  P99 = `p99`,
  MIN = `min`,
  MAX = `max`,
  HISTOGRAM = `histogram`,
  UNIQUE_VALUES = `unique_values`,
  MOST_FREQUENT = `most_frequent`,
  // per-user operators
  PER_USER_TOTAL = `total`,
  PER_USER_AVERAGE = `average`,
  PER_USER_MIN = `min`,
  PER_USER_MAX = `max`,
  PER_USER_UNIQUE_VALUES = `unique_values`,
}

export enum NUMERIC_MATH_TYPES {
  P25 = `p25`,
  P75 = `p75`,
  P90 = `p90`,
  P99 = `p99`,
}

export enum XAU_MATH_TYPES {
  DAU = `dau`,
  WAU = `wau`,
  MAU = `mau`,
}

export const EVENT_MATH_LABELS = {
  TOTAL: `Total`,
  UNIQUE: `Unique`,
  SESSIONS: `Sessions`,
  AVERAGE: `Average`,
  MEDIAN: `Median`,
  P25: `P25`,
  P75: `P75`,
  P90: `P90`,
  P99: `P99`,
  MIN: `Min`,
  MAX: `Max`,
  DAU: `DAU`,
  WAU: `WAU`,
  MAU: `MAU`,
};

export const PROPERTY_MATH_LABELS = {
  ...EVENT_MATH_LABELS,
  TOTAL: `Sum`,
};

export type MATH_TYPE = PRIMATIVE_MATH_TYPES | NUMERIC_MATH_TYPES | XAU_MATH_TYPES;

export const XAU_ONLY_MATH_TYPES = Object.values(XAU_MATH_TYPES);

export const ALL_MATH_TYPES = {
  ...PRIMATIVE_MATH_TYPES,
  ...NUMERIC_MATH_TYPES,
  ...XAU_MATH_TYPES,
};

export const SPECIAL_SERIES_NAMES = {
  $cohort: `Cohort`,
  $event: `Event`,
  $people: `People`,
};

export const FEATURE_GATES_UNLIMITED = 9223372036854776000; // python max int, aka featureGates.unlimited
export const MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT = 365 * 5; // maximum upgradeable historic data days

export const RESOURCE_LABELS = Object.freeze({
  USER: `User`,
  GROUP: `Group`,
  EVENT: `Event`,
});

export enum CLUSTER_NAMES {
  MAIN_CLUSTER_NAME = `mixpanel-prod-1`,
  EU_CLUSTER_NAME = `mixpanel-prod-eu`,
}

export enum CLUSTER_IDS {
  MIXPANEL_PROD_1_ID = 1,
  MIXPANEL_PROD_EU_ID = 5,
}

export const TRACKING_URL_BY_CLUSTER = {
  [CLUSTER_NAMES.MAIN_CLUSTER_NAME]: `https://api.mixpanel.com`,
  [CLUSTER_NAMES.EU_CLUSTER_NAME]: `https://api-eu.mixpanel.com`,
};

export enum PRODUCTION_DOMAINS {
  EU = `eu.mixpanel.com`,
  US = `mixpanel.com`,
}

export const DISABLED_DUE_TO_CLASSIFIED_DATA = `Disabled due to Classified Data`;

export const ERROR_DUE_TO_MISSING_PROEPRTY_MAPPING = `Unable to Display Data`;

export enum DownloadMode {
  None = 0,
  File = 1,
  Slack = 2,
}

/**
 * These correspond to ReportNames in reports/constants.py
 */
export enum ReportName {
  AbTest = `ab_test`,
  Campaign = `campaign`,
  Cohorts = `cohorts`,
  Dashboards = `dashboards`,
  EntityManagement = `entity-management`,
  EventBinding = `event_binding`,
  ExperimentReporting = `experiment-reporting`,
  Explore = `explore`,
  Flows = `flows`,
  Funnels = `funnels`,
  Impact = `impact`,
  Insights = `insights`,
  Integrations = `integrations`,
  Journeys = `journeys`,
  JqlConsole = `jql-console`,
  Lexicon = `lexicon`,
  Library = `library`,
  Live = `live`,
  Messages = `messages`,
  MpPlatform = `mpplatform`,
  Profile = `profile`,
  Retention = `retention`,
  Revenue = `revenue`,
  Segmentation = `segmentation`,
  Setup = `setup`,
  Signal = `signal`,
  Transformations = `transformations`,
  UserSettings = `user_settings`,
  Views = `views`,
  Workspaces = `workspaces`,
}

/**
 * Restricted report names slightly differ from ReportName which is based on url part.
 * These correspond to FEATURE_GATES['restricted_reports'] usage in configurations/settings_global.py
 * e.g `jql` instead of `jql-console`
 */
export enum RestrictedReport {
  ABTesting = `ab_testing`,
  Campaign = `campaign`,
  Cohorts = `cohorts`,
  ExperimentReporting = `experiment-reporting`,
  Flows = `flows`,
  Formulas = `formulas`,
  Impact = `impact`,
  JqlConsole = `jql`,
  Messages = `messages`,
  Predict = `predict`,
  Revenue = `revenue`,
  Signal = `signal`,
  VisualJourneys = `visual_journeys`,
}

/*
 * These correspond to FEATURE_GATES['restrict_entity_saving'] usage in configurations/settings_global.py
 */
export enum RestrictEntitySaving {
  CustomProperties = `custom_properties`,
  Cohorts = `cohorts`,
  LookupTables = `lookup_tables`,
}

/**
 * These correspond to ProjectFeatureFlag in project/models.py
 */
export enum ProjectFeatureFlag {
  B2B = `b2b_analytics`,
  B2BAnalytics = `b2b_analytics`,
  B2BCohorts = `b2b_cohorts`,
  BehavioralProperties = `behavioral_properties`,
  Cohorts = `cohorts`,
  CustomBucketsFunnels = `custom_buckets_funnels`,
  CustomBucketsRetention = `custom_buckets_retention`,
  CustomProperty = `custom_property`,
  DashboardManagement = `dashboard_management`,
  DataClassification = `data_classification`,
  DataDefinitionsFetch = `data_definitions_fetch`,
  DataManagement = `data_management`,
  DashboardsFunnelStepsV2 = `dash_funnel_steps_v2`,
  Dev = `dev`,
  DimensionPropertyLookupTable = `dim_prop_lookup_table`,
  DisableEntityManagementCohorts = `disable_em_cohorts`,
  EntityManagementCohorts = `entity_mgmnt_cohorts`,
  FestiveModeStage = `festive_mode_stage`,
  FirstEventTime = `first_event_time`,
  FirstTimeEventFilters = `first_time_event_filter`,
  FlowsMultipleAnchors = `flows_multiple_anchors`,
  FlowsMultipleAnchors2 = `flows_multiple_anchors_2`,
  FlowsNewQueryBuilder = `flows_new_qb`,
  FlowsTimePercentiles = `flows_time_percentiles`,
  FunnelsChartToggle = `funnels_chart_toggle`,
  FunnelsFrequencyChart = `funnels_frequency_chart`,
  FunnelsFrequencyConversionRate = `funnels_freq_conv_rate`,
  FunnelsDropOffAnalysis = `funnel_drop_off_analysis`,
  FunnelsNoQueryBuilder = `no_funnels_query_builder`,
  FunnelsQueryBuilder = `funnels_query_builder`,
  FunnelsSessions = `funnels_sessions`,
  FunnelsTrendTypeView = `funnels_trend_type_view`,
  HideExperimentConfidence = `hide_experiment_confidence`,
  HideMenuApps = `hide_menu_apps`,
  HideMenuMessages = `hide_menu_messages`,
  HideReportDataAudit = `hide_data_audit`,
  HideReportJqlConsole = `hide_report_jql_console`,
  HideReportRevenue = `hide_report_revenue`,
  HideReportSegmentation = `hide_report_segmentation`,
  IdManagementUIUpdates = `id_management_ui_updates`,
  InlineCohorts = `inline_cohorts`,
  IntegrationsDataViews = `integrations_dataviews`,
  IntegrationsNewUI = `integrations_new_ui`,
  LookupTablesInlineMap = `lookup_tables_inline_map`,
  LookupTableSharing = `lookup_table_sharing`,
  MessagesAndExperiments = `messages_and_experiments`,
  NavRedesignV1 = `nav_redesign_v1`,
  NewDatePicker = `new_date_picker`,
  NewDatePresets = `new_date_presets`,
  NotifsCampaigns = `notifs_campaigns`,
  NotifsVisualJourneys = `notifs_visual_journeys`,
  Omnisearch = `omnisearch`,
  PipelinesNewUI = `pipelines_new_ui`,
  QBMetadataSearch = `qb_metadata_search`,
  QBNewMetricsMode = `qb_new_metrics_mode`,
  QBPerLineProperties = `qb_per_line_properties`,
  QBNewFilters = `qb_new_filters`,
  QBCohorts = `qb_cohorts`,
  QTS = `query_time_sampling`,
  QTSByDefault = `qts_by_default`,
  RetentionChartView = `retention_chart_view`,
  ReadSchemasOnly = `read_schemas_only`,
  ShowLast7Days = `show_last_7_days`,
  ShowUberWelcome = `show_uber_welcome`,
  ShowUberWelcomeProd = `show_uber_welcome_prod`,
  Sst = `sst`,
  UnlimitedEmailDigests = `unlimited_dash_digests`,
}

/**
 * These correspond to Whitelist in reports/models.py
 */
export enum UserFeatureFlag {
  Dev = `dev`,
  RollupProjects = `rollup-projects`,
  Sampling = `sampling`,
  FestiveModeStage = `festive-mode-stage`,
}

/**
 * These correspond to UserFlags.FLAGS in user/models.pyF
 */
export enum UserFlag {
  ClosedPlatformLearnLink = `CLOSED_PLATFORM_LEARN_LINK`,
  DashboardAddedReportOrSeenTooltip = `DASHBOARD_ADDED_REPORT_OR_SEEN_TOOLTIP`,
  MobileInappCustomIntro = `MOBILE_INAPP_CUSTOM_INTRO`,
  RecurringMessagesModal = `RECURRING_MESSAGES_MODAL`,
  ViewedInsightsCount = `VIEWED_INSIGHTS_COUNT`,
  ViewedQueryConsoleIntro = `VIEWED_QUERY_CONSOLE_INTRO`,
  ViewedRecurBetaModal = `VIEWED_RECUR_BETA_MODAL`,
  ViewedRecurV2BetaModal = `VIEWED_RECUR_V2_BETA_MODAL`,
  ViewedReportCount = `VIEWED_REPORT_COUNT`,

  JourneyTut = `JOURNEY_TUT_DONE`,
  MoveIndivCampaign = `VIEWED_MOVE_INDIV_CAMPAIGN_TOOLTIP`,
  MoveJourneyCampaign = `VIEWED_MOVE_JOURNEY_CAMPAIGN_TOOLTIP`,
  NotificationsTut = `NOTIFICATIONS_3_TUT_DONE`,
  OnboardingModal = `VIEWED_ONBOARDING_MODAL`,
  SMSPricingModal = `SMS_PRICING_MODAL`,
  UnreadIndividualTut = `NOTIFICATIONS_INDIVIDUAL_UNREAD_TUT`,
  UnreadJourneyTut = `NOTIFICATIONS_JOURNEY_UNREAD_TUT`,
}

/**
 * Features enabled for a certain kind of feature gated plan
 * Corresponds to FEATURE_GATES in configurations/settings_global.py
 */
export interface FeatureGatesValue {
  allow_custom_alerts: boolean;
  allow_formatted_export: boolean;
  allow_raw_data_export: boolean;
  allow_smart_hub: boolean;
  b2b_analytics: boolean;
  can_drop_events_and_properties: boolean;
  can_export_csv: boolean;
  can_hide_events_and_properties: boolean;
  can_merge_events_and_properties: boolean;
  can_segment_retention: boolean;
  can_use_datawarehouse_export: boolean;
  can_use_platform_github: boolean;
  data_classification: boolean;
  max_custom_apps: number;
  max_custom_events: number;
  max_dashboard_digests: number;
  max_data_history_days: number;
  max_insights_filters: number;
  max_org_members: number;
  max_project_members: number;
  max_projects: number;
  max_saved_reports: number;
  max_segmentation_filters: number;
  on_demand_analysis: boolean;
  restricted_reports: string[];
  restrict_entity_saving: string[];
  sharing: boolean;
  sso: boolean;
  workspaces: boolean;
}

export enum FeatureGate {
  AllowCustomAlerts = `allow_custom_alerts`,
  AllowFormattedExport = `allow_formatted_export`,
  AllowRawDataExport = `allow_raw_data_export`,
  AllowSmartHub = `allow_smart_hub`,
  B2BAnalytics = `b2b_analytics`,
  CanDropEventsAndProperties = `can_drop_events_and_properties`,
  CanExportCsv = `can_export_csv`,
  CanHideEventsAndProperties = `can_hide_events_and_properties`,
  CanMergeEventsAndProperties = `can_merge_events_and_properties`,
  CanSegmentRetention = `can_segment_retention`,
  CanUseDatawarehouseExport = `can_use_datawarehouse_export`,
  CanUsePlatformGithub = `can_use_platform_github`,
  DataClassification = `data_classification`,
  MaxCustomApps = `max_custom_apps`,
  MaxCustomEvents = `max_custom_events`,
  MaxDashboardDigests = `max_dashboard_digests`,
  MaxDataHistoryDays = `max_data_history_days`,
  MaxInsightsFilters = `max_insights_filters`,
  MaxOrgMembers = `max_org_members`,
  MaxProjectMembers = `max_project_members`,
  MaxProjects = `max_projects`,
  MaxSavedReports = `max_saved_reports`,
  MaxSegmentationFilters = `max_segmentation_filters`,
  OnDemandAnalysis = `on_demand_analysis`,
  RestrictedReports = `restricted_reports`,
  RestrictEntitySaving = `restrict_entity_saving`,
  Sharing = `sharing`,
  SSO = `sso`,
  Workspaces = `workspaces`,
}

/**
 * For non-PII use, add to STORAGE_ALLOWLIST in media/js/gdpr/storage.js
 */
export enum LocalStorageKey {
  // used by featuresStore to cache api response
  FeaturesCache = `$mp_features_cache`,
  // for creating messages from explore
  MessageTargetingFromExplore = `$mp_message_targeting_from_explore`,
  // used by Dashboards to remember the user's last opened dashboard ID for any project or workspace
  LastOpenedDashboard = `$mp_last_opened_dashboard`,
}

export const NEW_DATE_PRESETS_ROLLOUT_PERCENT = 10;
